/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {
  SyntheticEvent,
  useCallback,
  useRef,
  useState
} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Typography} from '@material-ui/core';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import {ColDef} from 'ag-grid-community';
import {translate} from '@ion/core/src/locale/locale';
import {error} from '@ion/core/src/logger/logger';
import {loadDevicePasswords} from '@ion/core/src/services/Connect';
import {GenericObject} from '@ion/core/src/types/entities';
import {CSVExportButton} from '@ion/dls/src/components/buttons/CSVExportButton';
import styles, {getClasses} from '@ion/dls/src/editors/editorStyles';
import {
  defaultTextFilterParams
} from '@ion/dls/src/grid/ColumnDefinitions';
import {
  resolveDefinitions
} from '@ion/dls/src/grid/ColumnResolver';
import {
  IonGrid, RowModelTypes
} from '@ion/dls/src/grid/IonGrid';
import {
  GridResult
} from '@ion/dls/src/grid/PaginationControls';
import {
  PAGE_SIZES
} from '@ion/dls/src/grid/settings';
import {useExtendStyles} from '@ion/dls/src/hooks/useExtendStyles';
import {MaterialExitCloseIcon} from '@ion/dls/src/icons/MaterialExitCloseIcon';
import {UserNotificationDialog} from '@ion/dls/src/notifications/UserNotificationDialog';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {DLSTheme, IconSize} from '@ion/dls/src/types/types';
import {ExportContext} from '@/types/redux';
import {exportDevicePassword} from '../../store/actions/exporter';

export interface DevicePasswordGridProps {
  onCloseDialog?: (event: SyntheticEvent<{}, Event>) => void;
  selectedIdentifiers?: any;
  identifierType?: string;
  isCtcAdmin?: boolean;
}

const styleOverrides = (theme: DLSTheme) => ({
  formPaper : {
    backgroundColor                : `${dlsColor('grey6')} !important`,
    minWidth                       : '1067px',
    maxWidth                       : '1067px',
    maxHeight                      : '668px',
    height                         : '668px',
    overflow                       : 'hidden',
    margin                         : 'auto',
    [theme.breakpoints.down('sm')] : {
      width     : '100%',
      maxHeight : '100% !important',
      margin    : '0px'
    }
  },
  formDialogContent : {
    overflow   : 'auto',
    paddingTop : '0px'
  },
  exportIcon : {
    display        : 'flex',
    justifyContent : 'end'
  }
});

/**
 * Grid to be rendered on the Device Password page
 */
export const DevicePasswordGrid = ({
  onCloseDialog,
  selectedIdentifiers,
  identifierType,
  isCtcAdmin = false
}: DevicePasswordGridProps) => {
  const dialogStyles = useExtendStyles(styles, styleOverrides);
  const classes      = dialogStyles();

  const dispatch                              = useDispatch();
  const ionGridRef                            = useRef<GenericObject>();
  const [gridApi, setGridApi]                 = useState(null);
  const [hasItems, setHasItems]               = useState<boolean>(false);
  const [refreshRequired, setRefreshRequired] = useState<boolean>(false);
  const [showError, setShowError]             = useState<boolean>(false);
  const [gridErrors, setGridErrors]           = useState([]);
  const {pending}                             = useSelector(({exporter}) => exporter.current);
  const isExporting                           = pending[ExportContext.devicePassword] === true;

  // Provides reference to the grid's api.
  const onGridReady = useCallback((api) => {
    setGridApi(api);
  }, []);

  /**
   * Returns a reference to the grids public api ..
   */
  const getDevicePasswordData = async function({
    page,
    pageSize,
    sort,
    callback,
    filter = {}
  }) {
    try {
      const {items, totalCount, errors} = await loadDevicePasswords(selectedIdentifiers, identifierType);
      const results = {items, totalCount};
      callback(results);
      setGridErrors(errors);
      setShowError(errors?.length);
      return results;
    } catch (err) {
      const errorArr = err?.response?.data?.response?.errors || [];
      setGridErrors(errorArr);
      setShowError(errorArr?.length);
      callback({items: [], totalCount: 0});
      error('Error occurred retrieving device password data.');
    }
  };

  /**
   * Converts a collection of Device Password and converts them to a list of items specific
   * for display within the Device Password Grid, while retaining all of the items existing
   * attributes,
  */
  const constructDevicePasswordGridRows = useCallback((devicePasswords: GenericObject[]): GridResult[] => {
    const gridItems = devicePasswords.map(asset => ({
      ...asset,
      name       : asset?.name || asset?.id,
      deviceType : asset?.assetType,
      year       : asset?.year,
      make       : asset?.make,
      model      : asset?.model
    })) as GridResult[];
    setHasItems(!!gridItems.length);
    return gridItems;
  }, []);

  const onExport = useCallback(() => {
    const contents = ionGridRef.current?.getAllDisplayedRowData();
    dispatch(exportDevicePassword(contents));
  }, [dispatch]);

  const onRefresh = useCallback(() => {
    setRefreshRequired(true);
  }, []);

  const onDismiss = useCallback(() => {
    setShowError(false);
  }, []);

  const getErrorMessage = useCallback(() => {
    const customError = translate('admin-devices:devicePassword.error.customMessage');
    const errorArr = [];
    gridErrors?.forEach((item) => {
      const {error = null} = item;
      if (error) {
        const {description} = error;
        const str = description || error;
        isCtcAdmin && !hasItems ? errorArr.push(customError) : errorArr.push(str);
      } else {
        isCtcAdmin && !hasItems ? errorArr.push(customError) : errorArr.push(item);
      };
    });
    return errorArr.join('\n');
  }, [gridErrors]);

  return (
    <Dialog open
      onBackdropClick={null}
      classes={getClasses(classes)}>
      <DialogTitle classes={{
        root : classes.formDialogTitle
      }}>
        <div className={classes.formTitle}>
          <div onClick={onCloseDialog} className={classes.closeButton}>
            <MaterialExitCloseIcon size={IconSize.medium}
              color={dlsColor('grey3')} />
          </div>
          <Typography component='h1' variant='h2'>
            {translate('admin-devices:devicePassword.grid.title')}
          </Typography>
        </div>
      </DialogTitle>
      <DialogContent className={classes.formDialogContent}>
        {hasItems && (
          <div className={classes.exportIcon}>
            <CSVExportButton onExport={onExport} isExporting={isExporting} devicePwdInfo/>
          </div>
        )}
        <div className={classes.gridContainer}>
          <IonGrid omitHeader
            ref={ionGridRef}
            rowModelType={RowModelTypes.ClientSide}
            loadItems={getDevicePasswordData}
            augmentItems={constructDevicePasswordGridRows}
            columnDefinitions={getColumnDefinitions()}
            refreshRequired={refreshRequired}
            setRefreshRequired={setRefreshRequired}
            checkboxesEnabled={false}
            showHeaderCheckbox={false}
            rowMenusEnabled={false}
            viewSubGroups={false}
            onGridReady={onGridReady}
            pageOffset
            pageSizes={PAGE_SIZES}
            isEnableCellTextSelection={isCtcAdmin} />
        </div>
        {gridErrors?.length > 0 && (
          <UserNotificationDialog show={showError}
            capitalize
            title={translate('admin-devices:devicePassword.grid.error')}
            message={getErrorMessage()}
            dismissLabel={translate('common:ok')}
            onDismiss={onDismiss} />
        )}
      </DialogContent>
    </Dialog>
  );
};

/**
 * Returns column definitions for desktop device password grid
 */
const getColumnDefinitionsDesktop = (): ColDef[] => (
  [{
    headerName : translate('admin-devices:devicePassword.grid.esn'),
    field      : 'esn',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : false
  },
  {
    headerName : translate('admin-devices:devicePassword.grid.imei'),
    field      : 'imei',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : false
  }, {
    headerName : translate('admin-devices:devicePassword.grid.mac'),
    field      : 'mac',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : false
  },
  {
    headerName : translate('admin-devices:devicePassword.grid.atCommands'),
    field      : 'atCmdSms',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : false
  },
  {
    headerName : translate('admin-devices:devicePassword.grid.user'),
    field      : 'user',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : false
  },
  {
    headerName : translate('admin-devices:devicePassword.grid.wifi'),
    field      : 'wifi0',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : false
  },
  {
    headerName : translate('admin-devices:devicePassword.grid.gui'),
    field      : 'gui1',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : false
  },
  {
    headerName : translate('admin-devices:devicePassword.grid.guiEng'),
    field      : 'guiEngr',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : false
  },
  {
    headerName : translate('admin-devices:devicePassword.grid.utilityKey'),
    field      : 'ukey',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : false
  }]
);

/**
 * Returns column definitions for mobile device password grid
 */
const getColumnDefinitionsMobile = (): ColDef[] => (
  [{
    headerName   : translate('admin-assets:grid.columns.name'),
    field        : 'name',
    sortable     : true,
    filter       : 'text',
    resizable    : true,
    unSortIcon   : true,
    hide         : false,
    filterParams : defaultTextFilterParams
  },
  {
    headerName : translate('admin-assets:grid.columns.type'),
    field      : 'assetType',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : false
  }, {
    headerName   : translate('admin-assets:grid.columns.year'),
    field        : 'year',
    sortable     : false,
    filter       : 'text',
    resizable    : true,
    unSortIcon   : true,
    hide         : false,
    filterParams : defaultTextFilterParams
  },
  {
    headerName : translate('admin-assets:grid.columns.make'),
    field      : 'make',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : false
  },
  {
    headerName : translate('admin-assets:grid.columns.model'),
    field      : 'model',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : false
  },
  {
    headerName : translate('admin-assets:grid.columns.lastModifiedOn'),
    field      : 'lastModifiedOn',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : false
  }]
);

const getColumnDefinitions = () => ({
  desktop : resolveDefinitions(getColumnDefinitionsDesktop()),
  mobile  : resolveDefinitions(getColumnDefinitionsMobile())
});

export default DevicePasswordGrid;
